class AnalyticsApi {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async getRevenueSummary ({ fromDate, toDate, organisationId, location, productSku }) {
    if (!this.pgConnection) {
      const err = new Error('Postgres connection is required')
      err.status = 500
      throw err
    }

    // location should be defined here, otherwise it would have thrown an error in agave, but this is to be extra safe
    if (!location) {
      const err = new Error('location is required')
      err.status = 400
      throw err
    }

    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const toDateExclusiveUtc = new Date(toDateUtc)
    toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)

    const locationScope = resolveLocationScope(location)
    const params = [
      organisationId,
      fromDateUtc.toISOString(),
      toDateExclusiveUtc.toISOString()
    ]
    const whereClauses = []

    if (locationScope.scopeType !== 'national') {
      const exactIndex = params.length + 1
      const descendantIndex = params.length + 2
      whereClauses.push(`
          AND (
            dl.fsid = $${exactIndex}
            OR dl.fsid LIKE $${descendantIndex}
          )
      `)
      params.push(locationScope.location, `${locationScope.location}:%`)
    }

    if (productSku) {
      whereClauses.push(`AND si.product_sku_id = $${params.length + 1}`)
      params.push(productSku)
    }

    const query = `
      WITH filtered AS (
        SELECT
        si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(*)::int AS total_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NOT NULL)::int AS priced_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NULL)::int AS missing_price_rows,
        COALESCE(SUM(units_sold), 0)::numeric AS units_sold_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (WHERE unit_price_at_sale IS NOT NULL),
          0
        )::numeric AS revenue_total
      FROM filtered
    `

    const { rows } = await this.pgConnection.query(query, params)
    const row = rows[0] || {}

    return {
      fromDate,
      toDate,
      organisationId,
      location: locationScope.location,
      productSku: productSku || null,
      totalRows: Number(row.total_rows || 0),
      pricedRows: Number(row.priced_rows || 0),
      missingPriceRows: Number(row.missing_price_rows || 0),
      unitsSoldTotal: Number(row.units_sold_total || 0),
      revenueTotal: Number(row.revenue_total || 0)
    }
  }
}

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/
const LOCATION_SDP_REGEX = /:sdp:[^:]+$/i

function parseDateOnlyUtcOrThrow (value, fieldName) {
  if (!DATE_ONLY_REGEX.test(value)) {
    throw new Error(`${fieldName} must be in YYYY-MM-DD format`)
  }

  const date = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  if (date.toISOString().slice(0, 10) !== value) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  return date
}

function resolveLocationScope (location) {
  if (location === 'national') {
    return {
      location: 'national',
      scopeType: 'national'
    }
  }

  const scopeType = LOCATION_SDP_REGEX.test(location) ? 'sdp' : 'regional'
  return {
    location: location,
    scopeType: scopeType
  }
}

module.exports = AnalyticsApi
